import React from 'react';
import Dialog from '../Dialog';

interface Comment {
    id: string;
    comment: string;
    userId?: {
        name: string;
    };
    createdAt?: string;
}

interface CommentsSectionProps {
    comments: Comment[];
    isLoadingComments: boolean;
    showCommentDialog: boolean;
    setShowCommentDialog: React.Dispatch<React.SetStateAction<boolean>>;
    commentText: string;
    setCommentText: React.Dispatch<React.SetStateAction<string>>;
    commentError: string;
    setCommentError: React.Dispatch<React.SetStateAction<string>>;
    handleCommentSubmit: () => Promise<void>;
    isSubmitting: boolean;
    userRole: string;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
    comments,
    isLoadingComments,
    showCommentDialog,
    setShowCommentDialog,
    commentText,
    setCommentText,
    commentError,
    setCommentError,
    handleCommentSubmit,
    isSubmitting,
    userRole
}) => {
    return (
        <div className='bg-white rounded-md shadow mb-4'>
            <div className='p-4 sm:p-6'>
                <div className="flex flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-5">
                    <h2 className='text-[20px] font-[700]'>Comments</h2>
                    {userRole !== 'interRepre' && (
                        <button
                            className='btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]'
                            onClick={() => setShowCommentDialog(true)}
                            id="write-comment-button"
                        >
                            <span className=""> + </span>
                            <span className="ml-1 hidden sm:inline">
                                Write a new Comment
                            </span>
                        </button>
                    )}
                </div>
                {isLoadingComments && (
                    <div className="py-4 text-center">
                        <div className="w-6 h-6 border-2 border-t-2 border-gray-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading comments...</p>
                    </div>
                )}{!Array.isArray(comments) || comments.length === 0 ? (
                    <p className="text-[16px] text-center text-gray-500 py-4">
                        No comments available
                    </p>
                ) : (
                    <div className="space-y-4">
                        {comments.map((comment: Comment, index: number) => {
                            if (!comment?.id || !comment?.comment) return null;

                            return (
                                <div key={comment.id} className={`border-b border-gray-300 ${index === comments.length - 1 ? 'border-b-0' : ''}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="text-[16px] text-gray-800">{comment.comment}</p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                By {comment.userId?.name || 'Unknown'} | {
                                                    comment.createdAt ?
                                                        new Date(comment.createdAt).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }) : 'Unknown date'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Dialog
                isOpen={showCommentDialog}
                width="w-[600px]"
                padding="px-6 py-6"
                onClose={() => setShowCommentDialog(false)}
                className="z-[60]"
                id="add-comment-dialog"
            >
                <div className="text-center">
                    <h3 className="text-2xl font-semibold mb-6">Add New Comment</h3>

                    <div className="mb-6">
                        <textarea
                            className={`w-full px-4 py-3 text-sm border ${commentError ? 'border-red-500' : 'border-gray-300'
                                } rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black min-h-[120px]`}
                            placeholder="Enter your comment here..."
                            value={commentText}
                            onChange={(e) => {
                                setCommentText(e.target.value);
                                if (e.target.value.trim()) {
                                    setCommentError('');
                                }
                            }}
                            id="comment-textarea"
                        />
                        {commentError && (
                            <p className="text-sm text-red-600 text-left mt-1">{commentError}</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => {
                                setShowCommentDialog(false);
                                setCommentText('');
                                setCommentError('');
                            }}
                            className="btn-secondary"
                            id="cancel-comment-button"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCommentSubmit}
                            className="btn-primary"
                            disabled={isSubmitting}
                            id="submit-comment-button"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default CommentsSection; 